import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import NoteTabs from '@/components/ui/NoteTabs';
import { ROUTES } from '@/configs/routes';
import { NotesTabsKeys, useSidebarNoteStore } from '@/stores/local/sidebar/SidebarNoteStore';
import { useDevices } from '@/utils/devices';

import AddNotesButton from '../AddNotesButton';
import FollowUpTabContent from '../followUp/FollowUpTabContent';
import InsightsTabContent from '../insights/InsightsTabContent';
import NotesTabContent from '../notes/NotesTabContent';

import MobileTabs from './MobileTabs';
import { BASE_PATH, FOLLOW_UP_PATH, INSIGHTS_PATH } from './config';

import s from './Tabs.module.scss';

type NoteTabsProps = {
  className?: string;
};

const TABS_CONTENT_COMPONENT_MAP: Record<NotesTabsKeys, React.FC> = {
  [NotesTabsKeys.notes]: NotesTabContent,
  [NotesTabsKeys.followUp]: FollowUpTabContent,
  [NotesTabsKeys.insights]: InsightsTabContent,
};

const Tabs: React.FC<NoteTabsProps> = ({ className }) => {
  const { isCompactViewport } = useDevices();

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { id } = useParams();
  const { tabs } = useSidebarNoteStore();
  const ContentComponent = TABS_CONTENT_COMPONENT_MAP[tabs.activeKey as NotesTabsKeys];

  React.useEffect(() => {
    if (pathname.includes(FOLLOW_UP_PATH)) {
      if (tabs.activeKey !== NotesTabsKeys.followUp) {
        tabs.setActiveKey(NotesTabsKeys.followUp);
      }
    } else if (pathname.includes(INSIGHTS_PATH)) {
      if (tabs.activeKey !== NotesTabsKeys.insights) {
        tabs.setActiveKey(NotesTabsKeys.insights);
      }
    } else if (pathname.includes(BASE_PATH)) {
      if (tabs.activeKey !== NotesTabsKeys.notes) {
        tabs.setActiveKey(NotesTabsKeys.notes);
      }
    }
  }, [pathname, tabs]);

  const getTabRoute = React.useCallback(
    (key: NotesTabsKeys) => {
      switch (key) {
        case NotesTabsKeys.notes:
          return ROUTES.organization.notebook.notes.create(String(id));

        case NotesTabsKeys.followUp:
          return ROUTES.organization.notebook.followUps.create(String(id));

        case NotesTabsKeys.insights:
          return ROUTES.organization.notebook.insights.create(String(id));

        default:
          return ROUTES.organization.notebook.create(String(id));
      }
    },
    [id],
  );

  const handleChange = React.useCallback(
    (key: string) => {
      const k = key as NotesTabsKeys;
      const target = getTabRoute(k);

      tabs.setActiveKey(k);
      if (target !== pathname) {
        navigate(target);
      }
    },
    [tabs, navigate, getTabRoute, pathname],
  );

  return (
    <div className={cn(s.root, className)}>
      <div className={s.root__header}>
        {isCompactViewport ? (
          <MobileTabs items={tabs.items} activeKey={tabs.activeKey} onChange={handleChange} />
        ) : (
          <NoteTabs items={tabs.items} activeKey={tabs.activeKey} onChange={handleChange} />
        )}
        <AddNotesButton className={s.root__button} activeTab={tabs.activeKey as NotesTabsKeys} />
      </div>
      <div className={s.root__content}>{ContentComponent && <ContentComponent />}</div>
    </div>
  );
};

export default observer(Tabs);
